import graphqlClient from "@/GQLClient";
import type { SummarizedNews } from "@/graphql";
import {
  Delete_Type,
  DeleteNewsDocument,
  type EntityPagerFilter,
  GetExternalNewsDocument,
  GetInternalNewsOnMyInstitutionDocument,
  GetSummarizedExternalNewsDocument,
  GetSummarizedInternalNewsDocument,
  Module_Type,
  type NewsInput,
  type NewsUnion,
  SaveNewsDocument,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { IPagerResult } from "@/interfaces";
import type { IdType } from "@/types";

const getNews = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedNews>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedInternalNewsDocument,
    variables: {
      filter,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.news) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.news;
    return {
      edges: edges.map((item) => item.node as SummarizedNews),
      pageInfo,
    };
  }

  throw new SentryGraphQLError(`api.news.getNews`, response);
};

const getNewsDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetInternalNewsOnMyInstitutionDocument,
    variables: {
      filter: {
        first: 1,
        ids,
      },
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.news) {
    const { edges } = response.data.institutions.getMyInstitution.news;
    return edges.map((item) => item.node as NewsUnion).at(0);
  }

  throw new SentryGraphQLError(`api.news.getNewsDetail`, response);
};

const getExtarnalNews = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedNews>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedExternalNewsDocument,
    variables: {
      type: Module_Type.News,
      filter,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.externalContent;
    return {
      edges: edges.map((item) => item.node as SummarizedNews),
      pageInfo,
    };
  }

  throw new SentryGraphQLError(`api.news.getExtarnalNews`, response);
};

const getExternalNewsDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetExternalNewsDocument,
    variables: {
      type: Module_Type.News,
      filter: {
        first: 1,
        ids,
      },
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges } =
      response.data.institutions.getMyInstitution.externalContent;
    return edges.map((item) => item.node as NewsUnion).at(0);
  }

  throw new SentryGraphQLError(`api.news.getExternalNewsDetail`, response);
};

const save = async (news: NewsInput) => {
  const response = await graphqlClient.query({
    query: SaveNewsDocument,
    variables: {
      news,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.news?.save?.success) {
    return response;
  }

  throw new SentryGraphQLError(`api.news.save`, response);
};

const deleteItem = async (id: string, type: Delete_Type) => {
  const response = await graphqlClient.query({
    query: DeleteNewsDocument,
    variables: {
      id,
      type,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.news?.delete?.success) {
    return response;
  }

  throw new SentryGraphQLError(`api.news.deleteItem`, response);
};

export default {
  getNews,
  getNewsDetail,
  getExtarnalNews,
  getExternalNewsDetail,
  deleteItem,
  save,
};
